<script>
  import { createEventDispatcher, onDestroy } from "svelte"
  import { Dialog } from "../services/bootstrap.js"
  import uuid from "uuid-v4"

  export let id = `dialog-${uuid()}`
  export let isOpen = false
  export let title = "Modal Title"
  export let size = "md"
  export let fullContent = false
  export let backdrop = "static"
  export let keyboard = true

  const parseOption = (value) => value === "false" ? false : value === "true" ? true : value
  $: backdropOption = parseOption($$restProps["data-bs-backdrop"] ?? backdrop)
  $: keyboardOption = parseOption($$restProps["data-bs-keyboard"] ?? keyboard)

  const dispatch = createEventDispatcher()
  let element
  let instance
  let synchronizing = false
  let destroyed = false
  let pendingOpen = false
  let pendingOptions

  const synchronize = async (requestedOpen, requestedBackdrop, requestedKeyboard) => {
    pendingOpen = requestedOpen
    pendingOptions = { backdrop: requestedBackdrop, keyboard: requestedKeyboard }
    if (!element || synchronizing || destroyed) return
    synchronizing = true
    try {
      while (!destroyed) {
        const nextOpen = pendingOpen
        if (nextOpen) {
          // Aplicar la configuración actual antes de cada apertura.
          if (instance && !element.open) {
            instance.dispose()
            instance = undefined
          }
          instance = Dialog.getOrCreateInstance(element, pendingOptions)
          await instance.show()
        } else if (instance) {
          await instance.hide()
        }
        if (nextOpen === pendingOpen) break
      }
    } finally {
      synchronizing = false
      if (destroyed) instance?.dispose()
    }
  }

  $: if (element) synchronize(isOpen, backdropOption, keyboardOption)

  const closeModal = () => {
    isOpen = false
    dispatch("close")
  }

  const requestClose = () => {
    isOpen = false
  }

  const handleCancel = () => {
    if (keyboardOption) requestClose()
  }

  // Usar los comandos nativos con las transiciones y opciones de Bootstrap.
  const handleCommand = (event) => {
    if (!["show-modal", "close", "request-close"].includes(event.command)) return
    event.preventDefault()
    isOpen = event.command === "show-modal"
  }

  onDestroy(() => {
    destroyed = true
    if (!synchronizing) instance?.dispose()
  })
</script>

<dialog
  {...$$restProps}
  {id}
  data-bs-backdrop={backdropOption}
  data-bs-keyboard={keyboardOption}
  bind:this={element}
  class="dialog dialog-scrollable {size === 'md' ? '' : 'dialog-' + size}"
  aria-label={title}
  on:close={closeModal}
  on:cancel|preventDefault={handleCancel}
  on:command={handleCommand}
>
  {#if fullContent}
    <slot />
  {:else}
    <div class="dialog-header">
      <h1 class="dialog-title">{title}</h1>
      <button type="button" class="btn-close" commandfor={id} command="close" aria-label="Cerrar" on:click={requestClose} />
    </div>
    <div class="dialog-body">
      <slot />
    </div>
    {#if $$slots.footer}
      <div class="dialog-footer">
        <slot name="footer" />
      </div>
    {/if}
  {/if}
</dialog>

<style>
  /* Bootstrap mantiene el diálogo cerrado en el DOM para las transiciones.
     Evitar que su posición fuera de pantalla aumente la altura del documento. */
  .dialog {
    position: fixed;
  }
</style>
